<script setup lang="ts">
import { computed, inject, reactive, Ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { ProjektRozpocetPolozka, ZoPPMPriloha } from '/@src/client-portal'
import { VyzvaSpecificationPublicResponse } from '/@src/client-public'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import { newLineToLineBreak } from '/@src/utils/format'
import { usePrilohaDownload } from './usePrilohaDownload'

export interface PrilohyVZhrnuti {
  prilohy: ZoPPMPriloha[]
  polozky: ProjektRozpocetPolozka[]
  isValidSection: boolean
}

export interface ListinnaPriloha {
  cislo: string
  nazov: string
  popis: string
  isValidSection: false
}

const props = withDefaults(defineProps<PrilohyVZhrnuti>(), {
  prilohy: undefined,
  polozky: undefined,
})

const { t } = useI18n()
const steps = useSteps()
const kodIrz = inject<Ref<string>>('kodIrz')
const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')
const bearerTokenIS = inject<string | undefined>('bearerTokenIS')
const stavPodana = inject<boolean>('stavPodana', false)
const ziadostId = inject<Ref<string | undefined>>('ziadostId')

const povinnePrilohy = computed(() =>
  props?.prilohy?.filter(
    (priloha) =>
      priloha?.povinnaPrilohaRef?.povinnaPrilohaId &&
      priloha?.priloha?.dokumenty?.length > 0
  )
)

const dalsiePrilohy = computed(() =>
  props?.prilohy?.filter((priloha) => !priloha?.povinnaPrilohaRef?.povinnaPrilohaId)
)

const listinnePrilohy = reactive<ListinnaPriloha[]>([])

const getListinnePrilohy = () => {
  const listOfListinne = specResponse?.zoppmSpec?.povinnePrilohy?.filter((priloha) => {
    if (priloha.sposobPredlozenia === 'LISTINNE') {
      return priloha
    }
  })
  listOfListinne?.forEach((priloha) =>
    listinnePrilohy.push({
      nazov: priloha.povinnaPrilohaRef?.nazov ? priloha.povinnaPrilohaRef?.nazov : '',
      cislo: priloha.povinnaPrilohaRef?.cislo ? priloha.povinnaPrilohaRef?.cislo : '',
      popis: priloha.popis ? priloha.popis : '',
    })
  )
}

onMounted(() => {
  getListinnePrilohy()
})

const getDokument = usePrilohaDownload({
  stavPodana,
  ziadostId: ziadostId?.value,
  bearerTokenIS,
  kodIrz: kodIrz?.value,
})

function getPovinnaPriloha(priloha: ZoPPMPriloha) {
  const povinnaPriloha = specResponse?.zoppmSpec?.povinnePrilohy?.find(
    (povinna) =>
      povinna?.povinnaPrilohaRef?.povinnaPrilohaId?.toString() ===
      priloha?.povinnaPrilohaRef?.povinnaPrilohaId?.toString()
  )

  return povinnaPriloha
}
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-7">
    {{ t('zoppm.step13.mainHeading') }}
  </IDSKHeading>

  <ZhrnutieError v-if="isValidSection" />

  <template v-if="povinnePrilohy?.length > 0">
    <IDSKHeading as="h3" variant="m">
      {{ t('zoppm.zhrnutie.prilohy.prilohyPredpisaneVyzvou') }}
    </IDSKHeading>
    <template v-for="(priloha, key) in povinnePrilohy" :key="key">
      <div class="mb-6">
        <IDSKHeading as="h4" variant="s" class="mb-4">
          {{ key + 1 }}. {{ priloha?.povinnaPrilohaRef?.nazov }}
        </IDSKHeading>
        <IDSKParagraph
          v-if="getPovinnaPriloha(priloha)?.popis"
          class="mb-4"
          v-html="richTextToGovukHtml(getPovinnaPriloha(priloha)?.popis)"
        />
        <IDSKParagraph v-if="getPovinnaPriloha(priloha)?.sposobPredlozenia" class="mb-4">
          {{ t('zoppm.zhrnutie.prilohy.sposobPodania') }}:
          {{
            t(
              `zoppm.step0.prilohyTable.sposobPredlozenia["${
                getPovinnaPriloha(priloha)?.sposobPredlozenia
              }"]`
            )
          }}
        </IDSKParagraph>

        <template v-for="dokument in priloha?.priloha?.dokumenty" :key="dokument">
          <IDSKLink
            href="#"
            class="govuk-!-display-block mb-2"
            no-visited-state
            @click.prevent="
              getDokument(
                dokument.dokumentRef.identifikatorUlozisko,
                dokument.dokumentRef.verziaUlozisko
              )
            "
          >
            {{ `${dokument.nazov}.${dokument.format}` }}
          </IDSKLink>
        </template>
      </div>
    </template>

    <div v-if="!stavPodana" class="d-flex align-items-right">
      <IDSKButton
        variant="secondary"
        type="button"
        :data-test-id="`zoppm-prilohy-zhrnutie-edit`"
        @click="
          () =>
            steps.setStep(NazvyKrokov['prilohy'], {
              ref: NazvyKrokov['zhrnutie'],
            })
        "
      >
        {{ t('zoppm.zhrnutie.editBtn') }}
      </IDSKButton>
    </div>
  </template>

  <template v-if="dalsiePrilohy?.length > 0">
    <IDSKDivider v-if="povinnePrilohy?.length > 0" class="mb-8" />

    <IDSKHeading as="h3" variant="m">
      {{ t('zoppm.zhrnutie.prilohy.dalsiePrilohy') }}
    </IDSKHeading>

    <template v-for="(priloha, key) in dalsiePrilohy" :key="key">
      <div class="mb-6">
        <IDSKHeading as="h4" variant="s" class="mb-4">
          {{ key + 1 }}. {{ priloha?.priloha?.nazov }}
        </IDSKHeading>
        <IDSKParagraph
          v-if="priloha?.priloha?.poznamka"
          class="mb-4"
          v-html="newLineToLineBreak(priloha?.priloha?.poznamka)"
        />

        <template v-for="dokument in priloha?.priloha?.dokumenty" :key="dokument">
          <IDSKLink
            href="#"
            class="govuk-!-display-block mb-2"
            no-visited-state
            @click.prevent="
              getDokument(
                dokument.dokumentRef.identifikatorUlozisko,
                dokument.dokumentRef.verziaUlozisko
              )
            "
          >
            {{ `${dokument.nazov}.${dokument.format}` }}
          </IDSKLink>
        </template>
      </div>
    </template>

    <div v-if="!stavPodana" class="d-flex align-items-right">
      <IDSKButton
        variant="secondary"
        type="button"
        class="mb-0"
        :data-test-id="`zoppm-prilohy-zhrnutie-edit`"
        @click="
          () =>
            steps.setStep(NazvyKrokov['prilohy'], {
              ref: NazvyKrokov['zhrnutie'],
            })
        "
      >
        {{ t('zoppm.zhrnutie.editBtn') }}
      </IDSKButton>
    </div>
  </template>

  <template v-if="listinnePrilohy.length > 0">
    <IDSKHeading as="h3" variant="m">
      {{ t('zoppm.zhrnutie.prilohy.listinnePrilohy') }}
    </IDSKHeading>
    <IDSKParagraph>{{ t('zoppm.zhrnutie.prilohy.listinnePopis') }}</IDSKParagraph>
    <div v-for="item in listinnePrilohy" :key="item.cislo">
      <IDSKParagraph
        ><strong
          ><span>{{ item.cislo }}. {{ item.nazov }}</span></strong
        ></IDSKParagraph
      >
      <IDSKParagraph v-html="item.popis"></IDSKParagraph>
    </div>
  </template>

  <hr class="mb-8 mt-8" />
</template>
